import { useMemo } from 'react';
import type { PermissionCode } from '@clarix/shared-types';
import { usePermissionsStore } from './permission-store';

/**
 * Phase-0 shell hook — minimal `{ has, codes }` surface used by shell
 * guards (e.g. `RequirePermission`). Backed by the rbac permission store
 * once the service layer hydrates it.
 *
 * For the richer helper surface (canBase, canForProject, tabPermissions,
 * etc.) used by the projects app, import `usePermission` from this
 * package instead.
 */
export function usePermissions(): {
  has: (code: PermissionCode) => boolean;
  codes: ReadonlySet<PermissionCode>;
} {
  const loaded = usePermissionsStore((s) => s.loaded);
  const permissions = usePermissionsStore((s) => s.permissions);

  const codes = useMemo(() => {
    const set = new Set<PermissionCode>();
    if (!loaded) return set;
    for (const [code, granted] of Object.entries(permissions)) {
      if (granted === true) set.add(code);
    }
    return set;
  }, [loaded, permissions]);

  return {
    codes,
    has: (code) => (loaded ? permissions[code] === true : false),
  };
}
